// 무의존 미니 마크다운 렌더러 — 공고 서술 필드용(목록·소제목·굵게·링크·문단).
//   외부 라이브러리 없이(배포 rsync 가 package.json/node_modules 제외) 동작. XSS 안전: 먼저 escape 후 변환.

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// 인라인: **굵게**, [텍스트](url), 맨URL, <br> 줄바꿈(표 셀 내 다중행)
function inline(s: string): string {
  return s
    .replace(/&lt;br\s*\/?&gt;/gi, "<br>") // esc 된 <br> 복원 (셀 안 줄바꿈)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" style="color:#2C5CFF;text-decoration:underline">$1</a>')
    // 이미 a 태그로 감싼 url 은 제외하고 맨 URL 만 링크화
    .replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, '$1<a href="$2" target="_blank" rel="noreferrer" style="color:#2C5CFF;text-decoration:underline">$2</a>');
}

// GFM 표: 구분행(| --- | :--: |) 판별 + 셀 분리
function isTableSep(line: string): boolean {
  return line.includes("-") && /^\|?[\s:|-]+\|?$/.test(line);
}
function splitCells(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

function mdToHtml(src: string): string {
  const lines = esc(src).split(/\r?\n/);
  const out: string[] = [];
  // 들여쓰기 기반 중첩 목록 스택. 각 레벨의 <li> 는 열린 채 두고(자식 목록을 그 안에 중첩),
  //   같은/얕은 레벨 항목이 오거나 목록이 끝날 때 </li></tag> 로 닫는다.
  const stack: { tag: "ul" | "ol"; indent: number }[] = [];
  const UL = `<ul style="margin:4px 0;padding-left:20px">`;
  const OL = `<ol style="margin:4px 0;padding-left:22px">`;
  const closeAllLists = () => {
    while (stack.length) { out.push("</li>"); out.push(`</${stack.pop()!.tag}>`); }
  };
  const pushItem = (html: string, ordered: boolean, indent: number) => {
    // 현재보다 깊은 레벨은 닫는다.
    while (stack.length && stack[stack.length - 1].indent > indent) {
      out.push("</li>"); out.push(`</${stack.pop()!.tag}>`);
    }
    const top = stack[stack.length - 1];
    if (top && top.indent === indent) {
      out.push("</li>"); // 같은 레벨 → 이전 항목 닫고 새 항목
      if ((top.tag === "ol") !== ordered) { // 같은 레벨인데 종류가 다르면 목록 교체
        out.push(`</${top.tag}>`); stack.pop();
        const tag = ordered ? "ol" : "ul"; out.push(ordered ? OL : UL); stack.push({ tag, indent });
      }
    } else {
      const tag = ordered ? "ol" : "ul"; out.push(ordered ? OL : UL); stack.push({ tag, indent }); // 더 깊음(또는 최초) → 중첩 시작
    }
    out.push(`<li style="margin:3px 0">${html}`);
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i].replace(/\t/g, "  ");
    const indent = raw.match(/^ */)![0].length;
    const line = raw.trim();
    if (!line) { closeAllLists(); continue; }
    let m: RegExpMatchArray | null;
    // 표: 현재 줄에 '|' 가 있고 다음 줄이 구분행이면 표 블록으로 렌더 (깨짐 방지 + 가로 스크롤)
    if (line.includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1].trim())) {
      closeAllLists();
      const header = splitCells(line);
      i += 2; // 헤더 + 구분행 건너뜀
      const raw: string[][] = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes("|")) {
        raw.push(splitCells(lines[i].trim()));
        i++;
      }
      i--; // for 루프의 i++ 보정
      // 셀 안 줄바꿈으로 한 행이 여러 줄로 쪼개진 경우 병합(LLM 표 출력 흔한 아티팩트):
      //   직전 행이 header 열수보다 셀이 적으면 이번 줄은 그 행의 continuation → 경계 셀을 <br>로 잇고 나머지 셀 이어붙임.
      //   (병합 후 열수가 header 를 넘지 않을 때만 — 정상 짧은 행 오병합 방지)
      const rows: string[][] = [];
      for (const r of raw) {
        const prev = rows[rows.length - 1];
        if (prev && prev.length < header.length && prev.length + r.length - 1 <= header.length && r.length > 0) {
          prev[prev.length - 1] += "<br>" + r[0];
          for (let k = 1; k < r.length; k++) prev.push(r[k]);
        } else {
          rows.push(r);
        }
      }
      // 열 수 = 헤더/행 중 최대 → 셀 누락 없이(패딩) 렌더
      const cols = Math.max(header.length, ...rows.map((r) => r.length));
      // 세로 병합(rowspan): 같은 열에서 연속으로 같은 값(빈 셀 포함)인 셀을 하나로 합친다.
      //   관공서 공고 표의 '구분/항목' 열(여러 행에 걸친 병합셀)이 GFM 로 평탄화되며 값이 반복되거나
      //   빈 칸으로 쪼개진 것을 복원 → 원본 병합표 모양으로 렌더. (데이터는 평범한 GFM 유지, 렌더만 병합)
      const norm = (r: number, c: number) => (rows[r]?.[c] ?? "").trim();
      const rspan = rows.map(() => new Array(cols).fill(1));
      const vskip = rows.map(() => new Array(cols).fill(false));
      for (let c = 0; c < cols; c++) {
        let r = 0;
        while (r < rows.length) {
          const v = norm(r, c);
          let k = r + 1;
          while (k < rows.length && norm(k, c) === v) k++;
          if (k - r > 1) { rspan[r][c] = k - r; for (let j = r + 1; j < k; j++) vskip[j][c] = true; }
          r = k;
        }
      }
      const th = Array.from({ length: cols })
        .map((_, ci) => `<th style="border:1px solid #e4e8f6;padding:6px 10px;background:#f1f3ff;text-align:left;font-weight:700;color:#0E1726">${inline(header[ci] ?? "")}</th>`)
        .join("");
      const trs = rows
        .map((r, ri) => "<tr>" + Array.from({ length: cols }).map((_, ci) => {
          if (vskip[ri][ci]) return ""; // 위 셀에 병합됨 → 생략
          const rs = rspan[ri][ci] > 1 ? ` rowspan="${rspan[ri][ci]}"` : "";
          return `<td${rs} style="border:1px solid #e4e8f6;padding:6px 10px;vertical-align:top">${inline(r[ci] ?? "")}</td>`;
        }).join("") + "</tr>")
        .join("");
      out.push(
        `<div style="overflow-x:auto;margin:8px 0"><table style="border-collapse:collapse;width:100%;font-size:14px"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></div>`,
      );
      continue;
    }
    // 소제목 (#, ##, ###)
    if ((m = line.match(/^#{1,4}\s+(.*)/))) {
      closeAllLists();
      out.push(`<h4 style="font-size:15px;font-weight:700;margin:12px 0 4px;color:#0E1726">${inline(m[1])}</h4>`);
      continue;
    }
    // 목록: - * • ○ · / ①.. / 1. 1)  (들여쓰기로 중첩)
    if ((m = line.match(/^(?:[-*•○·]|[①-⑳]|\d+[.)])\s+(.*)/))) {
      const ordered = /^\d/.test(line);
      pushItem(inline(m[1]), ordered, indent);
      continue;
    }
    // 들여쓴 비불릿 줄 = 직전 목록 항목의 연속(설명·부가 텍스트). 목록을 닫지 말고 현재 열린 <li> 안에 붙인다.
    //   (그대로 <p> 로 내보내면 closeAllLists 가 매 줄 중첩목록을 무너뜨림 — 제출서류의 '(문서명)' 연속 줄 등)
    if (stack.length && indent > 0) {
      out.push(`<div style="margin:2px 0">${inline(line)}</div>`);
      continue;
    }
    closeAllLists();
    out.push(`<p style="margin:7px 0">${inline(line)}</p>`);
  }
  closeAllLists();
  return out.join("");
}

export function Markdown({ text }: { text: string }) {
  if (!text || !text.trim()) return null;
  return (
    <div
      style={{ fontSize: 15, lineHeight: 1.7, color: "#434654" }}
      dangerouslySetInnerHTML={{ __html: mdToHtml(text.trim()) }}
    />
  );
}
